//@ts-check
import React, {Component} from 'react';
import {default as TextInput} from '@sinclair-digital/sbg-storyline-components/src/components/forms/TextInput';
import {default as Field} from '@sinclair-digital/sbg-storyline-components/src/components/Field';
import {default as Icon} from '@sinclair-digital/sbg-storyline-components/src/components/Icon';
import {TooltipWrapper} from '@sinclair-digital/sbg-storyline-components/src/components/TooltipWrapper';
import {getFeatureFlag} from '@sinclair-digital/sbg-storyline-sdk/lib/storyline/featureFlags';
import {appContextShape} from '@sinclair-digital/sbg-storyline-sdk/lib/storyline/AppContext';
import {func, shape, node, string} from 'prop-types';
import {facebook} from 'react-icons-kit/fa/facebook';
import {twitter} from 'react-icons-kit/fa/twitter';
import {googlePlus} from 'react-icons-kit/fa/googlePlus';
import {pinterest} from 'react-icons-kit/fa/pinterest';
import {instagram} from 'react-icons-kit/fa/instagram';
import {youtube} from 'react-icons-kit/fa/youtube';
import {questionCircle} from 'react-icons-kit/fa/questionCircle';
import {FacebookDiagnostics} from 'components/FacebookDiagnostics';
import classNames from 'classnames';
import {socialShape} from './Social';
import styles from './SocialSettings.scss';

const BadgedContainer = ({icon, iconClassName, children}) => {
    return (
        <div className={styles.BadgedContainer}>
            <div className={styles.badgedInput}>{children}</div>
            <div className={classNames(styles.badge, iconClassName)}>{icon}</div>
        </div>
    );
};
BadgedContainer.propTypes = {
    icon: node.isRequired,
    iconClassName: string,
    children: node.isRequired,
};

class SocialSettings extends Component {
    static propTypes = {
        appContext: shape(appContextShape),
        socialSettings: shape(socialShape).isRequired,
        onChange: func.isRequired,
        onValidatedInputChange: func.isRequired,
    };

    renderFacebookDiagnosticBadge() {
        const featureFlag = this.props.appContext.getFeatureFlag('facebookDiagnosticsBadge');
        if (!featureFlag) {
            return undefined;
        }

        const {livestreamFacebookAppId, fbPages} = this.props.socialSettings;
        return <FacebookDiagnostics applicationId={livestreamFacebookAppId} pageId={fbPages} />;
    }

    render() {
        const {
            facebookUrl,
            twitterUrl,
            googlePlusUrl,
            pinterestUrl,
            instagramFeedUrl,
            youtubeChannelUrl,
            facebookAppId,
            livestreamFacebookAppId,
            twitterAppId,
            googlePlusAppId,
            fbPages,
            youtubeLivestreamId,
            youtubeChannelId,
        } = this.props.socialSettings;
        const isYoutubeLivestreamEnabled = getFeatureFlag('live.youtubeLivestream');

        return (
            <div className={styles.SocialSettings}>
                <div className={styles.triplet}>
                    {/* FACEBOOK URL  */}
                    <Field label="Facebook URL">
                        <BadgedContainer icon={<Icon icon={facebook} />} iconClassName={styles.facebook}>
                            <TextInput
                                onChange={this.props.onValidatedInputChange}
                                errorMessage={facebookUrl.errorMessage}
                                name="facebookUrl"
                                type="text"
                                className="sda-input-text-full sda-social-setting-input"
                                value={facebookUrl.value}
                                maxLength="1024"
                                placeholder="https://www.facebook.com/"
                            />
                        </BadgedContainer>
                    </Field>

                    {/* TWITTER URL  */}
                    <Field label="Twitter URL">
                        <BadgedContainer icon={<Icon icon={twitter} />} iconClassName={styles.twitter}>
                            <TextInput
                                onChange={this.props.onValidatedInputChange}
                                errorMessage={twitterUrl.errorMessage}
                                name="twitterUrl"
                                type="text"
                                className="sda-input-text-full sda-social-setting-input"
                                value={twitterUrl.value}
                                maxLength="1024"
                                placeholder="https://twitter.com/"
                            />
                        </BadgedContainer>
                    </Field>

                    {/* GOOGLE+ URL  */}
                    <Field label="Google+ URL">
                        <BadgedContainer icon={<Icon icon={googlePlus} />} iconClassName={styles.googleplus}>
                            <TextInput
                                onChange={this.props.onValidatedInputChange}
                                errorMessage={googlePlusUrl.errorMessage}
                                name="googlePlusUrl"
                                type="text"
                                className="sda-input-text-full sda-social-setting-input"
                                value={googlePlusUrl.value}
                                maxLength="1024"
                                placeholder="https://plus.google.com/"
                            />
                        </BadgedContainer>
                    </Field>

                    {/* PINTEREST URL  */}

                    <Field label="Pinterest URL">
                        <BadgedContainer icon={<Icon icon={pinterest} />} iconClassName={styles.pinterest}>
                            <TextInput
                                onChange={this.props.onValidatedInputChange}
                                name="pinterestUrl"
                                errorMessage={pinterestUrl.errorMessage}
                                type="text"
                                className="sda-input-text-full sda-social-setting-input"
                                value={pinterestUrl.value}
                                maxLength="1024"
                                placeholder="https://www.pinterest.com/"
                            />
                        </BadgedContainer>
                    </Field>

                    {/* INSTAGRAM FEED  */}
                    <Field label="Instagram Feed">
                        <BadgedContainer icon={<Icon icon={instagram} />} iconClassName={styles.instagram}>
                            <TextInput
                                onChange={this.props.onValidatedInputChange}
                                name="instagramFeedUrl"
                                errorMessage={instagramFeedUrl.errorMessage}
                                type="text"
                                className="sda-input-text-full sda-social-setting-input"
                                value={instagramFeedUrl.value}
                                maxLength="1024"
                                placeholder="https://instagram.com/"
                            />
                        </BadgedContainer>
                    </Field>

                    {/* YOUTUBE CHANNEL  */}
                    <Field label="YouTube Channel">
                        <BadgedContainer icon={<Icon icon={youtube} />} iconClassName={styles.youtube}>
                            <TextInput
                                onChange={this.props.onValidatedInputChange}
                                name="youtubeChannelUrl"
                                type="text"
                                errorMessage={youtubeChannelUrl.errorMessage}
                                className="sda-input-text-full sda-social-setting-input"
                                value={youtubeChannelUrl.value}
                                maxLength="1024"
                                placeholder="https://www.youtube.com/user/"
                            />
                        </BadgedContainer>
                    </Field>
                </div>
                <div className={styles.triplet}>
                    {/* FACEBOOK APP ID  */}
                    <Field label="Facebook App ID" className={styles.flexGrow1}>
                        <TextInput
                            onChange={this.props.onChange}
                            name="facebookAppId"
                            type="text"
                            className="sda-input-text-full"
                            value={facebookAppId}
                            maxLength="1024"
                            placeholder=""
                        />
                    </Field>

                    {/* FACEBOOK LiveStream APP ID  */}
                    <Field label="LiveStream Facebook App ID" className={styles.flexGrow1}>
                        <TextInput
                            onChange={this.props.onChange}
                            name="livestreamFacebookAppId"
                            type="text"
                            className="sda-input-text-full"
                            value={livestreamFacebookAppId}
                            maxLength="1024"
                            placeholder=""
                        />
                    </Field>

                    {/* TWITTER APP ID  */}

                    <Field label="Twitter App ID" className={styles.flexGrow1}>
                        <TextInput
                            onChange={this.props.onChange}
                            name="twitterAppId"
                            type="text"
                            className="sda-input-text-full"
                            value={twitterAppId}
                            maxLength="1024"
                            placeholder=""
                        />
                    </Field>

                    {/* GOOGLE+ APP ID  */}
                    <Field label="Google+ App ID" className={styles.flexGrow1}>
                        <TextInput
                            onChange={this.props.onChange}
                            name="googlePlusAppId"
                            type="text"
                            className="sda-input-text-full"
                            value={googlePlusAppId}
                            maxLength="1024"
                            placeholder=""
                        />
                    </Field>
                </div>

                <div className={styles.triplet}>
                    <Field
                        label={
                            <span>
                                Facebook Page ID
                                {this.renderFacebookDiagnosticBadge()}
                            </span>
                        }
                        className={styles.flexGrow1}>
                        <TextInput
                            onChange={this.props.onChange}
                            name="fbPages"
                            type="text"
                            className="sda-input-text-full"
                            value={fbPages}
                            maxLength="1024"
                            placeholder=""
                        />
                    </Field>
                    {isYoutubeLivestreamEnabled &&
                        <>
                            <Field
                                label={
                                    <span>
                                        Youtube ID
                                        <TooltipWrapper position="top" tooltip="Edgio ID">
                                            <Icon icon={questionCircle} />
                                        </TooltipWrapper>
                                    </span>
                                }
                                className={styles.flexGrow1}>
                                <TextInput
                                    onChange={this.props.onChange}
                                    name="youtubeLivestreamId"
                                    type="text"
                                    className="sda-input-text-full"
                                    value={youtubeLivestreamId}
                                    placeholder=""
                                />
                            </Field>
                            <Field label="Youtube Channel ID" className={styles.flexGrow1}>
                                <TextInput
                                    onChange={this.props.onChange}
                                    name="youtubeChannelId"
                                    type="text"
                                    className="sda-input-text-full"
                                    value={youtubeChannelId}
                                    placeholder=""
                                />
                            </Field>
                        </>
                    }
                </div>
            </div>
        );
    }
}

export default SocialSettings;
